import React, { useState, useRef, useEffect } from 'react';
import SignatureModal from '../SignatureModal';

interface InformedConsentPsychotherapyProps {
  onAccept: (signature: string) => void;
}

const InformedConsentPsychotherapy: React.FC<InformedConsentPsychotherapyProps> = ({ onAccept }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setHasScrolledToBottom(isAtBottom);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
  };

  const handleSignatureComplete = (signatureData: string, signatureType: 'drawn' | 'typed', typedName?: string) => {
    setSignature(signatureData);
    setShowSignatureModal(false);
  };

  const handleSubmit = () => {
    if (signature) {
      onAccept(signature);
    }
  };

  return (
    <div className="app">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            The Real Therapists of Atlanta
          </h1>
          <p style={{ color: '#6CA6C1', margin: '0 0 1rem 0', fontSize: '1rem' }}>
            626 Main Street | Palmetto, GA. 30268 | (470).250.9113
          </p>
          <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
            Informed Consent for Psychotherapy
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>
            Please read the consent form below and scroll to the bottom to continue
          </p>
        </div>

        {/* Consent Content */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          border: '1px solid #6CA6C1',
          marginBottom: '2rem'
        }}>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
              height: '400px',
              overflowY: 'auto',
              padding: '2rem',
              color: '#d1d5db',
              fontSize: '0.9rem',
              lineHeight: '1.6'
            }}
          >
            <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>
              INFORMED CONSENT FOR PSYCHOTHERAPY
            </h2>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              General Information
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              The therapeutic relationship is unique in that it is a highly personal and at the same time, a contractual agreement. Given this, it is important for us to reach a clear understanding about how our relationship will work, and what each of us can expect. This consent will provide a clear framework for our work together. Feel free to discuss any of this with me. Please read and indicate that you have reviewed this information and agree to it by filling in the checkbox at the end of this document.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              The Therapeutic Process
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              You have taken a very positive step by deciding to seek therapy. The outcome of your treatment depends largely on your willingness to engage in this process, which may, at times, result in considerable discomfort. Remembering unpleasant events and becoming aware of feelings attached to those events can bring on strong feelings of anger, depression, anxiety, etc. There are no miracle cures. I cannot promise that your behavior or circumstance will change. I can promise to support you and do my very best to understand you and repeating patterns, as well as to help you clarify what it is that you want for yourself.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              Confidentiality
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              The session content and all relevant materials to the client's treatment will be held confidential unless the client requests in writing to have all or portions of such content released to a specifically named person/persons. Limitations of such client held privilege of confidentiality exist and are itemized below:
            </p>

            <ol style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem', color: '#d1d5db' }}>
              <li style={{ marginBottom: '0.75rem' }}>
                If a client threatens or attempts to commit suicide or otherwise conducts themselves in a manner in which there is a substantial risk of incurring serious bodily harm.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                If a client threatens grave bodily harm or death to another person.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                If the therapist has a reasonable suspicion that a client or other named victim is the perpetrator, observer of, or actual victim of physical, emotional or sexual abuse of children under the age of 18 years.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                Suspicions as stated above in the case of an elderly person who may be subjected to these abuses.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                Suspected neglect of the parties named in items #3 and #4.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                If a court of law issues a legitimate subpoena for information stated on the subpoena.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                If a client is in therapy or being treated by order of a court of law, or if information is obtained for the purpose of rendering an expert's report to an attorney.
              </li>
            </ol>

            <p style={{ marginBottom: '1.5rem' }}>
              Occasionally I may need to consult with other professionals in their areas of expertise in order to provide the best treatment for you. Information about you may be shared in this context without using your name.
            </p>

            <p style={{ marginBottom: '1.5rem' }}>
              If we see each other accidentally outside of the therapy office, I will not acknowledge you first. Your right to privacy and confidentiality is of the utmost importance to me, and I do not wish to jeopardize your privacy. However, if you acknowledge me first, I will be more than happy to speak briefly with you, but feel it appropriate not to engage in any lengthy discussions in public or outside of the therapy office.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              About the Therapist
            </h3>
            <p style={{ marginBottom: '2rem' }}>
              I am humbled to assist you with your therapeutic process. Therapy is a process, one that is catered to your needs. I am here to assist with this journey.
            </p>

            <p style={{ marginBottom: '1.5rem', fontWeight: 'bold', color: '#f9fafb', textTransform: 'uppercase' }}>
              BY SIGNING BELOW I AM AGREEING THAT I HAVE READ, UNDERSTOOD AND AGREE TO THE ITEMS CONTAINED IN THIS DOCUMENT.
            </p>

            <p style={{ marginBottom: '0', fontSize: '0.8rem', color: '#d1d5db' }}>
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Agreement Section */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #6CA6C1',
          marginBottom: '1.5rem'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              color: hasScrolledToBottom ? '#f9fafb' : '#6b7280',
              cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
            }}>
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={handleCheckboxChange}
                disabled={!hasScrolledToBottom}
                style={{
                  marginRight: '0.75rem',
                  width: '18px',
                  height: '18px',
                  cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
                }}
              />
              I have read, understood, and agree to the Informed Consent for Psychotherapy
            </label>
          </div>

          {/* Signature Section */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: isAgreed ? '#f9fafb' : '#6b7280',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>
              Digital Signature *
            </label>
            <button
              type="button"
              onClick={() => setShowSignatureModal(true)}
              disabled={!isAgreed}
              style={{
                width: '100%',
                padding: '3rem 1rem',
                borderRadius: '8px',
                border: '2px dashed #6CA6C1',
                background: isAgreed ? '#374151' : '#1f2937',
                color: isAgreed ? '#f9fafb' : '#6b7280',
                fontSize: '1rem',
                cursor: isAgreed ? 'pointer' : 'not-allowed',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              {signature ? (
                <div>
                  <div style={{ marginBottom: '0.5rem', color: '#10b981' }}>✓ Signature Captured</div>
                  <div style={{ fontSize: '0.875rem' }}>Click to update signature</div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '0.5rem' }}>📝</div>
                  <div>Click to add your digital signature</div>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleSubmit}
            disabled={!signature}
            style={{
              background: signature
                ? 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)'
                : '#6b7280',
              color: 'white',
              padding: '0.75rem 3rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: signature ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: signature
                ? '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
                : 'none'
            }}
          >
            Submit Consent
          </button>
        </div>

        {/* Progress Indicator */}
        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#065f46',
          borderRadius: '8px',
          border: '1px solid #10b981'
        }}>
          <div style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
            Progress:
            <span style={{ color: hasScrolledToBottom ? '#10b981' : '#6b7280', marginLeft: '0.5rem' }}>
              ✓ Read Consent
            </span>
            <span style={{ color: isAgreed ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Accept Terms
            </span>
            <span style={{ color: signature ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Digital Signature
            </span>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSignatureComplete={handleSignatureComplete}
          title="Informed Consent for Psychotherapy Signature"
          clientName="Client"
          previousSignature={signature ? {
            signatureData: signature,
            signatureType: 'drawn' as const
          } : undefined}
        />
      )}
    </div>
  );
};

export default InformedConsentPsychotherapy;